import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { joinLabeledValue } from '../../utils/joinLabeledValue';

interface AdminStorageViewToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  className?: string;
  /** 同一画面に複数置く場合の id 衝突回避 */
  inputId?: string;
  /**
   * compact: スイッチ＋xs はチップ文言／md+ は短文言（chrome・統計帯向け）
   * dense: 1行・チップ文言（可視・aria）。短文言は input title
   * default: スイッチ＋フルラベル
   */
  compact?: boolean;
  dense?: boolean;
  /** 画面文脈に合わせたラベル（未指定時は保管庫向け既定） */
  labelKey?: string;
  shortLabelKey?: string;
  /** dense / compact(md+) で OFF のときに見せる範囲ラベル（ON 時は shortLabel） */
  offShortLabelKey?: string;
  /** compact(xs/md): 可視チップ（全員 / 自分）。短文言は input title */
  chipLabelKey?: string;
  offChipLabelKey?: string;
  hintKey?: string;
}

/** 管理者向け：全ユーザー視点の表示切替（ヒントは title に集約して縦スペースを節約） */
export default function AdminStorageViewToggle({
  checked,
  onChange,
  className = '',
  inputId = 'admin-storage-view-all',
  compact = false,
  dense = false,
  labelKey = 'storage_room.admin_view_all_label',
  shortLabelKey = 'storage_room.admin_view_all_label_short',
  offShortLabelKey = 'sample.admin_view_scope_mine_short',
  chipLabelKey = 'sample.admin_view_scope_all_chip',
  offChipLabelKey = 'sample.admin_view_scope_mine_chip',
  hintKey = 'storage_room.admin_view_all_hint',
}: AdminStorageViewToggleProps) {
  const { t } = useTranslation();
  const hint = t(hintKey);
  const label = t(labelKey);
  const shortLabel = t(shortLabelKey, { defaultValue: label });
  const offShortLabel = t(offShortLabelKey, {
    defaultValue: t('sample.stats_my_samples_short', { defaultValue: 'Mine only' }),
  });
  const chipLabel = t(chipLabelKey, { defaultValue: 'All' });
  const offChipLabel = t(offChipLabelKey, { defaultValue: 'Mine' });
  const scopeText = checked ? shortLabel : offShortLabel;
  const chipText = checked ? chipLabel : offChipLabel;
  const controlTitle = joinLabeledValue(scopeText, hint);

  if (compact) {
    return (
      <div
        className={`sample-admin-view-compact d-flex align-items-center gap-1 min-w-0 ${className}`.trim()}
        title={hint}
      >
        <Form.Check type="switch" id={inputId} className="mb-0 flex-shrink-0">
          <Form.Check.Input
            type="checkbox"
            role="switch"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            aria-label={chipText}
            title={controlTitle}
          />
        </Form.Check>
        {/*
          可視は chip のみ（xs/md 共通）。scope の短文言は title に載せ、
          Dual span（d-md-none + d-none d-md-inline）だと textContent が
          「自分自分のサンプル」と連結されるのを避ける。
        */}
        <label
          htmlFor={inputId}
          className="sample-admin-view-compact-label mb-0"
          aria-hidden="true"
          title={controlTitle}
        >
          {chipText}
        </label>
      </div>
    );
  }

  if (dense) {
    return (
      <div
        className={`sample-admin-view-dense d-flex align-items-center gap-2 min-w-0 ${className}`.trim()}
        title={hint}
      >
        <Form.Check type="switch" id={inputId} className="mb-0 flex-shrink-0">
          <Form.Check.Input
            type="checkbox"
            role="switch"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            aria-label={chipText}
            title={controlTitle}
          />
        </Form.Check>
        <label
          htmlFor={inputId}
          className="sample-admin-view-dense-label mb-0 text-truncate"
          aria-hidden="true"
          title={controlTitle}
        >
          {chipText}
        </label>
      </div>
    );
  }

  return (
    <div className={`ps-1 overflow-visible ${className}`.trim()}>
      <Form.Check type="switch" id={inputId} className="mb-0 small">
        <Form.Check.Input
          type="checkbox"
          role="switch"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          title={hint}
        />
        <Form.Check.Label title={hint}>{label}</Form.Check.Label>
      </Form.Check>
    </div>
  );
}
